import { z } from "zod";
import { UserIncomeType, UserSignupPurpose, UserType } from "../types/user";

export const userProductTipsSchema = z.object({
    initialProductTipTransactions: z.string().datetime().optional(),
    initialProductTipInvoices: z.string().datetime().optional(),
    initialProductTipRules: z.string().datetime().optional(),
});

export const userEventsTriggeredSchema = z
    .object({
        initialCategorizationCompleted: z.boolean().optional(),
        categorizationAfterSubscriptionCompleted: z.boolean().optional(),
        emailVerified: z.boolean().optional(),
        applicationSubmitted: z.boolean().optional(),
    })
    .nullable();

export const userSettingsSchema = z.object({
    weeklyInsightsEnabledAt: z.string().datetime().nullable().optional(),
    monthlyInsightsEnabledAt: z.string().datetime().nullable().optional(),
    dailyTasksDigestEnabledAt: z.string().datetime().nullable().optional(),
});

export const userMetaSchema = z.object({
    autoMarkBlockedTaskAsDoneAt: z
        .string()
        .datetime({ offset: true })
        .nullable()
        .optional(),
});

export const userSchema = z.object({
    id: z.number(),
    name: z.string(),
    legalName: z.string().nullable(),
    preferredName: z.string().nullable(),
    email: z.string().nullable(),
    avatar: z.string().nullable(),
    firstName: z.string(),
    lastName: z.string(),
    phoneNumber: z.string().nullable(),
    unverifiedPhoneNumber: z.string().nullable(),
    phoneNumberDisplay: z.string().nullable(),
    dateOfBirth: z.coerce.date().nullable(),
    incomeType: z.array(z.nativeEnum(UserIncomeType)),
    onboardingComplete: z.boolean(),
    showBookkeepingReviewInvite: z.boolean(),
    bookkeepingReviewScheduled: z.boolean(),
    lastActivity: z.coerce.date(),
    iconColor: z.string(),
    savingsCreditLimit: z.number(),
    freeUsageLimit: z.number(),
    settings: userSettingsSchema,
    createdAt: z.coerce.date(),
    onboardingCompletedAt: z.coerce.date().nullable(),
    eventsTriggered: userEventsTriggeredSchema,
    stripeCustomerId: z.string().nullable(),
    productTips: userProductTipsSchema.nullable().optional(),
    bookedEnterpriseCall: z.boolean(),
    type: z.nativeEnum(UserType),
    signupPurpose: z.nativeEnum(UserSignupPurpose).nullable(),
});

export const userStatusResponseSchema = z.object({
    user: userSchema,
    plainHash: z.string().optional(),
});

export type UserSchema = z.infer<typeof userSchema>;
export type UserStatusResponseSchema = z.infer<typeof userStatusResponseSchema>;

export const createAvatarUploadUrlBodySchema = z.object({
    name: z.string(),
});

export const createAvatarUploadUrlResponseSchema = z.object({
    url: z.string(),
    path: z.string(),
});

export const updateAvatarBodySchema = z.object({
    avatarPath: z.string().nullable(),
});

export const updateProfileBodySchema = z
    .object({
        email: z.string().email().optional(),
        phoneNumber: z
            .string()
            .regex(/^\d{10}$/)
            .optional(),
        workspaceId: z.string().uuid().optional(),
        verificationCode: z.string().min(1),
        phoneOrEmail: z.string().optional(),
    })
    .refine((v) => v.email ?? v.phoneNumber, {
        message: "Either email or phoneNumber must be provided",
    });

export const addPhoneNumberBodySchema = z.object({
    phoneNumber: z.string().regex(/^\d{10}$/, "Must be a 10-digit number"),
});

export const updateUserBodySchema = z.object({
    incomeType: z.array(z.nativeEnum(UserIncomeType)).optional(),
    signupPurpose: z.nativeEnum(UserSignupPurpose).optional(),
    preferredName: z
        .string()
        .regex(/\S+/, "Preferred name cannot be empty or whitespace only")
        .optional(),
    unverifiedPhoneNumber: z
        .string()
        .regex(/^\d{10}$/, "Must be a 10-digit number")
        .optional(),
    showBookkeepingReviewInvite: z.boolean().optional(),
    bookkeepingReviewScheduled: z.boolean().optional(),
    bookedEnterpriseCall: z.boolean().optional(),
    settings: userSettingsSchema.optional(),
    eventsTriggered: userEventsTriggeredSchema.optional(),
});

export const updateUserMetaBodySchema = z.object({
    autoMarkBlockedTaskAsDoneAt: z
        .string()
        .datetime({ offset: true })
        .nullable(),
});

export const UserProductTipKey = z.enum([
    "initialProductTipTransactions",
    "initialProductTipInvoices",
    "initialProductTipRules",
]);

export type UserProductTipKey = z.infer<typeof UserProductTipKey>;

export const setUserProductTipBodySchema = z.object({
    key: UserProductTipKey,
    value: z.string().datetime(),
});

export type UpdateProfileBody = z.infer<typeof updateProfileBodySchema>;

export const userProductTipsResponseSchema = userProductTipsSchema;

export type UserProductTipsResponse = z.infer<
    typeof userProductTipsResponseSchema
>;
